// FilterState ⇔ URLSearchParams の往復。URL が single source of truth になるよう、
// 既定値と等しいキーは書き出さない（URL を短く保ち、既定値の変更にも自然に追従する）。
import { DEFAULT_FILTER_STATE, type FilterState, type HubKey } from "./filters";
import { DEFAULT_METRIC, isMetricKey } from "./metrics";

const HUB_KEYS: HubKey[] = ["tokyo", "osaka", "nagoya"];

function isHubKey(v: string): v is HubKey {
  return (HUB_KEYS as string[]).includes(v);
}

function parseNumberOrNull(v: string | null): number | null {
  if (v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function encodeFilterState(state: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  const d = DEFAULT_FILTER_STATE;

  if (state.metric !== DEFAULT_METRIC) params.set("m", state.metric);
  if (state.pref !== d.pref) params.set("pref", state.pref ?? "");
  if (state.floodMax !== d.floodMax) params.set("flood", String(state.floodMax));
  if (state.tsunamiMax !== d.tsunamiMax) params.set("tsunami", String(state.tsunamiMax));
  if (state.tsunamiInlandOnly !== d.tsunamiInlandOnly) params.set("tsunamiInland", "1");
  if (state.sedimentMax !== d.sedimentMax) params.set("sediment", String(state.sedimentMax));
  if (state.quakeMax !== d.quakeMax) params.set("quake", String(state.quakeMax));
  if (state.landPriceMax !== d.landPriceMax) params.set("landprice", String(state.landPriceMax));
  if (state.populationMin !== d.populationMin) params.set("popmin", String(state.populationMin));
  if (state.tempJanMin !== d.tempJanMin) params.set("tempjanmin", String(state.tempJanMin));
  if (state.tempAugMax !== d.tempAugMax) params.set("tempaugmax", String(state.tempAugMax));
  if (state.hub !== d.hub) params.set("hub", state.hub);
  if (state.hubDistMax !== d.hubDistMax) params.set("hubdist", String(state.hubDistMax));
  if (state.excludeUnmeasured !== d.excludeUnmeasured) params.set("measured", "1");

  return params;
}

export function decodeFilterState(params: URLSearchParams): FilterState {
  const m = params.get("m");
  const pref = params.get("pref");
  const hub = params.get("hub");

  return {
    metric: m !== null && isMetricKey(m) ? m : DEFAULT_FILTER_STATE.metric,
    pref: pref !== null && pref !== "" ? pref : DEFAULT_FILTER_STATE.pref,
    floodMax: parseNumberOrNull(params.get("flood")) ?? DEFAULT_FILTER_STATE.floodMax,
    tsunamiMax: parseNumberOrNull(params.get("tsunami")) ?? DEFAULT_FILTER_STATE.tsunamiMax,
    tsunamiInlandOnly: params.get("tsunamiInland") === "1",
    sedimentMax: parseNumberOrNull(params.get("sediment")) ?? DEFAULT_FILTER_STATE.sedimentMax,
    quakeMax: parseNumberOrNull(params.get("quake")) ?? DEFAULT_FILTER_STATE.quakeMax,
    landPriceMax: parseNumberOrNull(params.get("landprice")) ?? DEFAULT_FILTER_STATE.landPriceMax,
    populationMin: parseNumberOrNull(params.get("popmin")) ?? DEFAULT_FILTER_STATE.populationMin,
    tempJanMin: parseNumberOrNull(params.get("tempjanmin")) ?? DEFAULT_FILTER_STATE.tempJanMin,
    tempAugMax: parseNumberOrNull(params.get("tempaugmax")) ?? DEFAULT_FILTER_STATE.tempAugMax,
    hub: hub !== null && isHubKey(hub) ? hub : DEFAULT_FILTER_STATE.hub,
    hubDistMax: parseNumberOrNull(params.get("hubdist")) ?? DEFAULT_FILTER_STATE.hubDistMax,
    excludeUnmeasured: params.get("measured") === "1",
  };
}
